import { useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Check, ArrowRight, Users, UserCheck, Award, Layers, Network,
  CalendarClock, CalendarRange, Briefcase, MessageSquare, Search, Handshake, RefreshCw,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";
import EnquiryForm from "../components/EnquiryForm";

const ROLES = [
  { key: "pharmacist", icon: Users, color: "#10B981", label: "Clinical Pharmacists", text: "Experienced primary care pharmacists for medication reviews, prescribing support and day-to-day clinical workload." },
  { key: "senior_pharmacist", icon: UserCheck, color: "#06B6D4", label: "Senior Clinical Pharmacists", text: "Senior practitioners for complex caseloads, service leadership and mentorship of wider pharmacy teams." },
  { key: "independent_prescriber", icon: Award, color: "#F59E0B", label: "Independent Prescribing Pharmacists", text: "Prescriber-qualified pharmacists able to manage long-term conditions and prescribing decisions within competency." },
  { key: "technician", icon: Layers, color: "#EF4444", label: "Pharmacy Technicians", text: "Technicians supporting medicines reconciliation, repeat prescribing processes, audits and administrative workflows." },
];

const ENGAGEMENTS = [
  { icon: CalendarClock, color: "#10B981", title: "Short-term support", text: "Sessional cover and rapid gap-filling when your rota cannot wait — from single sessions to several weeks." },
  { icon: CalendarRange, color: "#06B6D4", title: "Long-term placements", text: "Consistent clinicians embedded in your team for months at a time, with continuity your patients can rely on." },
  { icon: Briefcase, color: "#F59E0B", title: "Project-based teams", text: "Clinical pharmacy teams assembled around a defined piece of work — a review programme, an audit, a backlog." },
];

const STEPS = [
  { icon: MessageSquare, color: "#10B981", title: "Tell us the roles you need", text: "Roles, hours, duration, on-site or remote — a two-minute enquiry is enough to start." },
  { icon: Search, color: "#06B6D4", title: "We match and propose", text: "We propose vetted, competency-checked clinicians with a clear workforce proposal and transparent rates." },
  { icon: Handshake, color: "#F59E0B", title: "Clinicians join your team", text: "Onboarded into your systems and supervision arrangements — working with your team, not around it." },
  { icon: RefreshCw, color: "#EF4444", title: "Ongoing support and review", text: "Regular check-ins, cover arrangements and the flexibility to scale the placement up or down." },
];

const Workforce = () => {
  const [selected, setSelected] = useState([]);
  const formRef = useRef(null);

  const toggleRole = (key) =>
    setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const pickAndGo = (key) => {
    setSelected((s) => (s.includes(key) ? s : [...s, key]));
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const requirementText = useMemo(() => {
    const labels = selected.map((k) => ROLES.find((r) => r.key === k)?.label).filter(Boolean);
    return labels.join("; ");
  }, [selected]);

  return (
    <main data-testid="workforce-page">
      <Seo
        title="Request Clinical Pharmacists & Technicians | NCS"
        description="Request clinical pharmacists, independent prescribers and pharmacy technicians — short-term, long-term or project-based. ARRS support where eligible."
      />
      {/* HERO */}
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Request clinical workforce</p>
          <h1
            data-testid="workforce-heading"
            className="mt-4 font-display font-extrabold tracking-tight leading-[1.04] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl max-w-4xl"
          >
            <MaskedLine delay={0.15}>Request Clinical Pharmacists and</MaskedLine>
            <MaskedLine delay={0.3}><span className="font-serif-accent">Pharmacy Technicians</span> for Your Team</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl"
          >
            National Clinical Services provides flexible clinical workforce solutions for GP
            practices, Primary Care Networks and other healthcare organisations. Request one role
            or several in a single enquiry — for short-term cover, long-term placements or
            project-based support, including ARRS workforce support where eligible.
          </motion.p>
        </div>
      </section>

      {/* ROLES */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              The Clinical Pharmacy Roles We Provide
            </h2>
            <p className="mt-3 text-base text-[#475569] max-w-2xl">
              Select the roles you are interested in — you can request multiple roles within the
              same enquiry.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {ROLES.map((r, i) => {
              const isSelected = selected.includes(r.key);
              return (
                <Reveal key={r.key} delay={(i % 2) * 0.08}>
                  <div
                    data-testid={`role-card-${r.key}`}
                    className={`relative h-full bg-white rounded-3xl p-8 border-2 transition-all duration-300 ${
                      isSelected
                        ? "border-[#06B6D4] shadow-[0_10px_30px_rgba(11,25,44,0.04)]"
                        : "border-[#E2E8F0] hover:border-[#CBD5E1]"
                    }`}
                  >
                    <button
                      data-testid={`role-select-${r.key}`}
                      onClick={() => toggleRole(r.key)}
                      aria-pressed={isSelected}
                      aria-label={`Select ${r.label}`}
                      className={`absolute top-6 right-6 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${
                        isSelected ? "bg-[#06B6D4] border-[#06B6D4] text-white" : "border-[#CBD5E1] text-transparent hover:border-[#06B6D4]"
                      }`}
                    >
                      <Check size={14} strokeWidth={3.5} />
                    </button>
                    <span
                      className="w-12 h-12 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: `${r.color}1A`, color: r.color }}
                    >
                      <r.icon size={22} />
                    </span>
                    <h3 className="mt-5 font-display text-xl font-bold text-[#0B192C] pr-8">{r.label}</h3>
                    <p className="mt-2.5 text-sm sm:text-base leading-relaxed text-[#475569]">{r.text}</p>
                    <button
                      data-testid={`role-request-${r.key}`}
                      onClick={() => pickAndGo(r.key)}
                      className="group mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#06B6D4] hover:text-[#0B192C] transition-colors"
                    >
                      Request This Role
                      <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-[#0B192C] rounded-3xl p-8" data-testid="teams-card">
                <Users size={24} className="text-[#10B981]" />
                <h3 className="mt-5 font-display text-xl font-bold text-white">Clinical Pharmacy Teams</h3>
                <p className="mt-2.5 text-sm sm:text-base leading-relaxed text-white/65">
                  Need more than individual roles? We assemble pharmacist-and-technician teams around
                  your workload, with coordination and oversight included.
                </p>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-3xl p-8" data-testid="arrs-workforce-card">
                <Network size={24} className="text-[#F59E0B]" />
                <h3 className="mt-5 font-display text-xl font-bold text-[#0B192C]">ARRS Workforce Support</h3>
                <p className="mt-2.5 text-sm sm:text-base leading-relaxed text-[#475569]">
                  Where your PCN is eligible, we support ARRS-funded clinical pharmacy roles.
                  Reimbursement always remains subject to NHS England scheme rules and your ICB.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ENGAGEMENT OPTIONS */}
      <section className="py-16 sm:py-20 bg-white border-y border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">Flexible by design</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              Flexible Engagement Options: Short-Term, Long-Term and Project-Based Support
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {ENGAGEMENTS.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.08}>
                <div className="h-full bg-[#FAFAFC] border border-[#E2E8F0] rounded-3xl p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
                  <span
                    className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${e.color}1A`, color: e.color }}
                  >
                    <e.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-[#0B192C]">{e.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{e.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#EF4444]">The process</p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
              How Requesting Clinical Workforce From NCS Works
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`workforce-step-${i + 1}`} className="relative bg-white border border-[#E2E8F0] rounded-3xl p-7 h-full">
                  <span className="absolute top-5 right-6 font-display text-5xl font-extrabold text-[#F1F5F9]">{i + 1}</span>
                  <span
                    className="w-11 h-11 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${s.color}1A`, color: s.color }}
                  >
                    <s.icon size={20} />
                  </span>
                  <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#475569]">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENQUIRY */}
      <section ref={formRef} className="py-20 sm:py-28 lg:py-32 bg-white border-t border-[#E2E8F0] scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#0F382C]">
              {selected.length > 0 ? `${selected.length} role${selected.length > 1 ? "s" : ""} selected` : "Get started"}
            </p>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C]">
              Request Your Clinical Workforce Proposal
            </h2>
            <p className="mt-4 text-base text-[#475569] max-w-xl mx-auto">
              Tell us the roles, hours, duration and working model — our team will respond with a
              workforce proposal. All placements are subject to competency checks, governance,
              availability and contract.
            </p>
          </Reveal>
          <Reveal>
            <EnquiryForm
              journey="workforce"
              defaultRequirement={requirementText}
              heading="Tell us about your workforce requirement"
            />
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Workforce;
